import { ZuruflexApiError, type FlagSummary, type StaleFlag } from '@zuruflex/api-client';
import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import { FLAG_SURFACES } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  type ColumnDef,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { flagStatus, type FlagStatus } from '../lib/flags.js';
import { useTableLabels } from '../lib/table-labels.js';

const ANY = 'any';
const STATUSES: FlagStatus[] = ['on', 'off', 'untouched'];

/**
 * Every feature flag the code declares, with the switch for each.
 *
 * The switch is here as well as on the detail page because turning a
 * feature off is the incident-time action, and an incident is not the time
 * to be clicking through to find it. Managers may flip it; changing who a
 * flag targets needs an owner, on the detail page.
 */
export function FlagsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canToggle = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const canEdit = user?.role === 'OWNER';

  const [q, setQ] = useState('');
  const [surface, setSurface] = useState<string>(ANY);
  const [status, setStatus] = useState<string>(ANY);
  const [resetting, setResetting] = useState<StaleFlag | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['flags'],
    queryFn: () => api.listFlags(),
  });

  const toggle = useMutation({
    mutationFn: (input: { flag: FlagSummary; enabled: boolean }) =>
      api.toggleFlag(input.flag.key, input.enabled, input.flag.version),
    onSuccess: (saved) => {
      queryClient.setQueryData(['flag', saved.key], saved);
      void queryClient.invalidateQueries({ queryKey: ['flags'] });
      void queryClient.invalidateQueries({ queryKey: ['flag', saved.key, 'history'] });
    },
  });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.flags ?? []).filter((flag) => {
      if (surface !== ANY && !(flag.surfaces as string[]).includes(surface)) return false;
      if (status !== ANY && flagStatus(flag) !== status) return false;
      if (!needle) return true;
      return [flag.key, flag.label, flag.description].some((text) =>
        text.toLowerCase().includes(needle),
      );
    });
  }, [data, q, surface, status]);

  const columns = useMemo<ColumnDef<FlagSummary>[]>(
    () => [
      {
        accessorKey: 'label',
        header: t('flags.column.flag'),
        cell: ({ row }) => (
          <div className="min-w-0">
            <Link
              to={`/flags/${encodeURIComponent(row.original.key)}`}
              onClick={(event) => event.stopPropagation()}
              className="font-medium underline-offset-2 hover:underline"
            >
              {row.original.label}
            </Link>
            <p className="font-mono text-xs text-text-muted">{row.original.key}</p>
          </div>
        ),
      },
      {
        accessorKey: 'type',
        header: t('common:field.type'),
        cell: ({ row }) => <Badge variant="outline">{t(`flags.type.${row.original.type}`)}</Badge>,
      },
      {
        id: 'surfaces',
        header: t('flags.column.surfaces'),
        cell: ({ row }) => (
          <div className="flex max-w-md flex-wrap gap-1">
            {row.original.surfaces.map((item) => (
              <Badge key={item}>{t(`flags.surface.${item}`)}</Badge>
            ))}
          </div>
        ),
      },
      {
        id: 'status',
        header: t('common:field.status'),
        cell: ({ row }) => {
          const flag = row.original;
          const state = flagStatus(flag);
          return (
            // The row navigates on click; the switch must not.
            <div className="flex items-center gap-3" onClick={(event) => event.stopPropagation()}>
              <Switch
                checked={flag.state.enabled}
                disabled={!canToggle || toggle.isPending}
                aria-label={t('flags.toggleLabel', { label: flag.label })}
                onCheckedChange={(enabled) => toggle.mutate({ flag, enabled })}
              />
              <Badge variant={state === 'on' ? 'success' : state === 'off' ? 'warning' : 'default'}>
                {t(`flags.status.${state}`)}
              </Badge>
            </div>
          );
        },
      },
      {
        id: 'rules',
        header: t('flags.column.rules'),
        cell: ({ row }) => <span className="tabular-nums">{row.original.state.rules.length}</span>,
      },
      {
        accessorKey: 'updatedAt',
        header: t('flags.column.changed'),
        cell: ({ row }) => (
          <span className="text-sm text-text-muted">
            {row.original.updatedAt
              ? formatDateTimeIn(row.original.updatedAt, locale)
              : t('flags.never')}
          </span>
        ),
      },
    ],
    [t, locale, canToggle, toggle],
  );

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const stale = data?.stale ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('flags.title')}
        description={t('flags.subtitle')}
      />

      {!canToggle ? (
        <Alert variant="info">{t('flags.readOnlyAgent')}</Alert>
      ) : !canEdit ? (
        <Alert variant="info">{t('flags.managerNote')}</Alert>
      ) : null}

      <ToggleError error={toggle.error} onReload={() => void refetch()} />

      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <FormField label={t('common:action.search')} className="xl:col-span-2">
          {(field) => (
            <Input
              {...field}
              placeholder={t('flags.searchPlaceholder')}
              value={q}
              onChange={(event) => setQ(event.target.value)}
            />
          )}
        </FormField>
        <FilterSelect
          label={t('flags.column.surfaces')}
          value={surface}
          onChange={setSurface}
          anyLabel={t('flags.anySurface')}
          options={FLAG_SURFACES.map((value) => ({ value, label: t(`flags.surface.${value}`) }))}
        />
        <FilterSelect
          label={t('common:field.status')}
          value={status}
          onChange={setStatus}
          anyLabel={t('flags.anyStatus')}
          options={STATUSES.map((value) => ({ value, label: t(`flags.status.${value}`) }))}
        />
      </div>

      <DataTable
        labels={tableLabels}
        columns={columns}
        data={rows}
        isLoading={isPending}
        onRowClick={(row) => navigate(`/flags/${encodeURIComponent(row.key)}`)}
        emptyMessage={t('flags.none')}
      />

      {stale.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('flags.stale.title')}</CardTitle>
            <CardDescription>{t('flags.stale.body')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border-default">
              {stale.map((row) => (
                <li key={row.key} className="flex flex-wrap items-center gap-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm font-medium">{row.key}</p>
                    <p className="text-xs text-text-muted">
                      {t('flags.stale.meta', {
                        version: row.version,
                        when: formatDateTimeIn(row.updatedAt, locale),
                      })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canEdit}
                    onClick={() => setResetting(row)}
                  >
                    <Trash2 />
                    {t('flags.stale.remove')}
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {resetting ? (
        <RemoveStaleDialog row={resetting} onClose={() => setResetting(null)} />
      ) : null}
    </div>
  );
}

/**
 * A 409 is not a failure so much as news: somebody else changed the flag
 * first. Offering the reload right there is the whole fix.
 */
function ToggleError({ error, onReload }: { error: unknown; onReload: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  if (!error) return null;
  const conflict = error instanceof ZuruflexApiError && error.status === 409;
  return (
    <Alert variant={conflict ? 'warning' : 'danger'}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span>{error instanceof ZuruflexApiError ? error.message : t('common:error.unexpected')}</span>
        {conflict ? (
          <Button size="sm" variant="outline" onClick={onReload}>
            {t('flags.reload')}
          </Button>
        ) : null}
      </div>
    </Alert>
  );
}

/** DELETE on a key the code no longer declares: nothing reads it, so this is tidying. */
function RemoveStaleDialog({ row, onClose }: { row: StaleFlag; onClose: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.resetFlag(row.key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flags'] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('flags.stale.confirmTitle', { key: row.key })}</DialogTitle>
          <DialogDescription>{t('flags.stale.confirmBody')}</DialogDescription>
        </DialogHeader>
        {remove.error ? (
          <Alert variant="danger">
            {remove.error instanceof ZuruflexApiError
              ? remove.error.message
              : t('common:error.unexpected')}
          </Alert>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="destructive"
            isLoading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {t('flags.stale.remove')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  anyLabel,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  anyLabel: string;
}) {
  return (
    <FormField label={label}>
      {(field) => (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={field.id}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{anyLabel}</SelectItem>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </FormField>
  );
}
